import type { WsEvent } from "@/api/ws";

/**
 * Shared circuit-breaker event helpers.
 *
 * <p>{@code breakerPayload} extracts the (code, detail) pair of a
 * {@code circuit_breaker.triggered} WS event. {@code collapseBreakerRuns} is the PV4
 * run-collapse (audit 2026-06-11: a night of spread blocks rendered as an unreadable
 * wall): CONSECUTIVE breaker rows with the same code merge into one synthetic row.
 * Grouping key = code ONLY — the detail varies per tick (spread 5.00↔7.00). Any
 * non-breaker row in between splits the run, so chronology stays exact.
 */
export function breakerPayload(ev: WsEvent): { code: string; detail: string } | null {
  if (ev.type !== "circuit_breaker.triggered") return null;
  const d = (ev.data ?? {}) as Record<string, unknown>;
  return {
    code: typeof d.code === "string" ? d.code : "UNKNOWN",
    detail: typeof d.detail === "string" ? d.detail : "",
  };
}

interface BreakerRunRow {
  kind: string;
  code?: string;
}

/**
 * Collapse consecutive {@code kind === "breaker"} rows sharing the same {@code code}
 * into one row produced by {@code merge(first, last, count)}. Rows of any other kind
 * pass through and split runs. Chronological input order is preserved.
 */
export function collapseBreakerRuns<T extends BreakerRunRow>(
  rows: T[],
  merge: (first: T, last: T, count: number) => T,
): T[] {
  const out: T[] = [];
  let run: T[] = [];
  const flush = () => {
    if (run.length === 0) return;
    if (run.length === 1) out.push(run[0]);
    else out.push(merge(run[0], run[run.length - 1], run.length));
    run = [];
  };
  for (const r of rows) {
    if (r.kind === "breaker" && r.code) {
      if (run.length > 0 && run[0].code !== r.code) flush();
      run.push(r);
      continue;
    }
    flush();
    out.push(r);
  }
  flush();
  return out;
}
